import { Immutable } from "immer";
import React from "react";
import { useStaticURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import type { ProductCallout as Value } from "@reactivated";

type IProps = Immutable<{
    value: Value | null;
    aboutSealyUrl?: string | null;
    warrantyServiceUrl?: string | null;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/callouts/product_callout.html`` (#33285). The block is a
 * value-less StaticBlock server-side; the serializer resolves the two
 * ``{% slugurl %}`` lookups into the value (page-body path) or into the
 * standalone template's extra props (styleguide path, where the StaticBlock
 * value is null), hence the dual-path href resolution.
 *
 * The legacy template emitted stray spaces inside the usa-icon ``src`` and
 * the about-sealy ``href`` attrs; those are dropped here.
 */
export const ProductCallout = (props: IProps): React.JSX.Element => {
    const staticURL = useStaticURL();
    const aboutSealyUrl =
        props.value?.about_sealy_url ?? props.aboutSealyUrl ?? "";
    const warrantyServiceUrl =
        props.value?.warranty_service_url ?? props.warrantyServiceUrl ?? "";
    return (
        <section className="callouts container">
            <div className="callouts__item">
                <figure className="callouts__icon">
                    <img
                        className="img-responsive"
                        src={`${staticURL}img/callout/icon-usa.png`}
                        alt={t`USA Icon`}
                        loading="lazy"
                        decoding="async"
                    />
                </figure>
                <div className="callouts__copy">
                    <h4 className="callouts__headline">
                        {t`Loved by American families for 130 years`}
                    </h4>
                    <p className="callouts__subtext">
                        {t`Our mattresses are designed and built in the U.S.A. When you buy a Sealy, you know you're supporting American jobs and families.`}
                    </p>
                    <a href={aboutSealyUrl} title="about-sealy">
                        {t`Learn More`}
                    </a>
                </div>
            </div>
            <div className="callouts__item">
                <figure className="callouts__icon">
                    <img
                        className="img-responsive"
                        src={`${staticURL}img/callout/icon-sealy-certified.png`}
                        alt={t`Sealy Certified Icon`}
                        loading="lazy"
                        decoding="async"
                    />
                </figure>
                <div className="callouts__copy">
                    <h4 className="callouts__headline">
                        {t`Backed by a 10 Year Warranty`}
                    </h4>
                    <p className="callouts__subtext">
                        {t`We go above and beyond industry testing standards to ensure our comfort is proven to last. Most Sealy mattresses come with a 10 year limited warranty.`}
                    </p>
                    <a href={warrantyServiceUrl} title={t`Warranty Service`}>
                        {t`Learn More`}
                    </a>
                </div>
            </div>
        </section>
    );
};
